import React, { useEffect, useState } from 'react';
import { Field, FilePicker, InfoTip, inputCls } from '../shared/FormKit';
import {
  emptyEngagementFields, EngagementProfileFields, companiesOf, profileSplitGaps, profileSplitJson, profileSplitOf,
} from '../shared/EngagementFormFields';
import {
  emptyScreeningAnswers, screeningAnswersToFields, screeningRequiredGaps, appendScreeningAnswers,
  ScreeningQuestionFields,
} from '../shared/ScreeningFields';
import { FormSection } from '../workflow/FormShell';
import EditableAttachments from '../workflow/EditableAttachments';
import { ScreeningCompaniesPicker } from '../../pages/WorkflowScreeningNew';
import { fetchJSON, API_BASE_URL } from '../../utils/api';
import { engagementDisplayName } from '../../utils/engagementName';
import { hintOf } from '../../utils/uploadKinds';

// Management → Screening Records — the ONE form definition behind the New page
// and the Detail page's Edit modal (docs/modules/workflow-screening.md §記錄層).
// Same blocks as the Screening request (companies → client profile → the six
// screening questions), plus what only the formal record carries: the
// completion date, the optional engagement link per company, and the
// screening PDF (replacing it supersedes the current one; the old version is
// kept). The client profile is optional here so a legacy screening can be
// recorded with what is known; the six answers, Ultimate Parent, the
// companies and the completion date are mandatory.

export const SCREENING_DOC_TYPE = 'SCREENING';

export const emptyScreeningRecordForm = () => ({
  ...emptyEngagementFields(),
  ...emptyScreeningAnswers(),
  groupId: '', entityIds: [], engagementLinks: {},
  completedOn: '', remarks: '',
});

const triToStr = (v) => (v === true ? 'yes' : v === false ? 'no' : '');

export const screeningRecordToForm = (r) => ({
  ...emptyScreeningRecordForm(),
  groupId: r.clientGroupId || '',
  entityIds: (r.entities || []).map((e) => e.entityId).filter(Boolean),
  engagementLinks: Object.fromEntries((r.entities || []).filter((e) => e.entityId && e.engagementId).map((e) => [e.entityId, e.engagementId])),
  service: r.serviceProvided === 'Other' ? 'Other' : (r.serviceProvided || ''),
  serviceOther: r.serviceProvided === 'Other' ? (r.serviceOther || '') : '',
  descriptionOfService: r.descriptionOfService || '',
  ultimateParent: r.ultimateParent || '',
  overseasOperations: triToStr(r.overseasOperations),
  consolidatedFs: r.consolidatedFs || '',
  isPie: triToStr(r.isPie),
  pieType: r.isPie === true ? (r.pieType || '') : '',
  pieTypeOther: r.isPie === true ? (r.pieTypeOther || '') : '',
  profileSplit: profileSplitOf(r.entities),
  proposedFees: r.proposedFees != null ? String(r.proposedFees) : '',
  proposedFeesCcy: r.proposedFeesCcy || 'SGD',
  ssicPrimary: r.ssicPrimary || '',
  ssicSecondary: r.ssicSecondary || '',
  principalActivity1: r.principalActivity1 || '',
  principalActivity2: r.principalActivity2 || '',
  secRegistered: triToStr(r.secRegistered),
  isGroupAudit: triToStr(r.isGroupAudit),
  underInvestigation: triToStr(r.underInvestigation),
  tcwgFeeCommunicated: triToStr(r.tcwgFeeCommunicated),
  ...screeningAnswersToFields(r),
  completedOn: r.completedOn || '',
  remarks: r.remarks || '',
});

export const screeningRecordGaps = (f) => {
  const gaps = [];
  if (!f.groupId) gaps.push({ key: 'clientGroup', label: 'Client Group' });
  else if (f.entityIds.length === 0) gaps.push({ key: 'entityNames', label: 'Company (At Least One)' });
  if (f.service === 'Other' && !String(f.serviceOther || '').trim()) gaps.push({ key: 'serviceOther', label: 'Other Service Description' });
  if (!String(f.ultimateParent || '').trim()) gaps.push({ key: 'ultimateParent', label: 'Ultimate Parent' });
  gaps.push(...profileSplitGaps(f));
  gaps.push(...screeningRequiredGaps(f));
  if (!f.completedOn) gaps.push({ key: 'completedOn', label: 'Completion Date' });
  return gaps;
};

const yn = (v) => (v === 'yes' ? 'true' : v === 'no' ? 'false' : '');

export const screeningRecordFormData = (f, { screeningFile = null, files = [], removeFileIds = [] } = {}) => {
  const form = new FormData();
  form.set('entityIds', JSON.stringify(f.entityIds));
  form.set('engagementLinks', JSON.stringify(
    Object.fromEntries(Object.entries(f.engagementLinks || {}).filter(([eid, gid]) => gid && f.entityIds.includes(Number(eid))))
  ));
  form.set('serviceProvided', f.service || '');
  if (f.service === 'Other') form.set('serviceOther', (f.serviceOther || '').trim());
  form.set('descriptionOfService', f.descriptionOfService || '');
  form.set('ultimateParent', f.ultimateParent || '');
  form.set('overseasOperations', yn(f.overseasOperations));
  form.set('consolidatedFs', f.consolidatedFs || '');
  form.set('isPie', yn(f.isPie));
  form.set('pieType', f.isPie === 'yes' ? (f.pieType || '') : '');
  form.set('pieTypeOther', f.isPie === 'yes' ? (f.pieTypeOther || '') : '');
  form.set('profileSplit', profileSplitJson(f));
  form.set('proposedFees', String(f.proposedFees ?? '').trim());
  form.set('proposedFeesCcy', f.proposedFeesCcy || 'SGD');
  form.set('ssicPrimary', f.ssicPrimary || '');
  form.set('ssicSecondary', f.ssicSecondary || '');
  form.set('principalActivity1', f.principalActivity1 || '');
  form.set('principalActivity2', f.principalActivity2 || '');
  form.set('secRegistered', yn(f.secRegistered));
  form.set('isGroupAudit', yn(f.isGroupAudit));
  form.set('underInvestigation', yn(f.underInvestigation));
  form.set('tcwgFeeCommunicated', yn(f.tcwgFeeCommunicated));
  appendScreeningAnswers(form, f);
  form.set('completedOn', f.completedOn || '');
  form.set('remarks', f.remarks || '');
  if (screeningFile) form.append('screening', screeningFile);
  (files || []).forEach((x) => form.append('files', x));
  if ((removeFileIds || []).length > 0) form.set('removeFileIds', JSON.stringify(removeFileIds));
  return form;
};

export const useScreeningRecordOptions = () => {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON('/api/qm/screening-records/options');
        if (!cancelled) setOptions(data);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, []);
  return { options, optionsError: error };
};

/** Companies of one group (the workflow picker), each with an optional engagement link. */
const CompaniesPicker = ({ f, set, options }) => {
  const { engagements = [] } = options || {};
  const link = (entityId, engagementId) => set('engagementLinks', { ...(f.engagementLinks || {}), [entityId]: engagementId || '' });
  return (
    <ScreeningCompaniesPicker
      f={f}
      set={set}
      options={options}
      caption="Companies — tick the ones this screening covers; link an engagement so its attachments page shows the screening document."
      onGroupChange={() => set('engagementLinks', {})}
      rowExtra={(e, ticked) => {
        const engs = engagements.filter((g) => g.entityId === e.id);
        if (!ticked || engs.length === 0) return null;
        return (
          <select
            value={(f.engagementLinks || {})[e.id] || ''}
            onChange={(ev) => link(e.id, ev.target.value ? Number(ev.target.value) : '')}
            className="ml-auto px-2 py-1 border border-slate-300 rounded text-xs bg-white"
            aria-label={`Engagement for ${e.entityName}`}
          >
            <option value="">No engagement link</option>
            {engs.map((g) => <option key={g.id} value={g.id}>{engagementDisplayName(g.engagementName, g.serviceDescription)}</option>)}
          </select>
        );
      }}
    />
  );
};

/**
 * The form body. `current` = the record being edited (its files show with
 * the replace / remove controls); null on the New page.
 */
export const ScreeningRecordFormFields = ({
  f, set, options, current = null,
  screeningFile, setScreeningFile, files, setFiles, removeIds = [], onToggleRemove = null,
}) => {
  const currentScreening = (current?.files || []).find((x) => x.docType === SCREENING_DOC_TYPE && !x.supersededAt);
  const supporting = (current?.files || []).filter((x) => x.docType !== SCREENING_DOC_TYPE);
  return (
    <>
      <FormSection step={1} title="Companies" hint={<>Pick the client group, then tick every company this screening covers. <InfoTip text="Register missing companies through Clients & Entities first." /></>}>
        <CompaniesPicker f={f} set={set} options={options} />
      </FormSection>
      <FormSection step={2} title="Service & Client Profile" hint="Record what is known.">
        <EngagementProfileFields f={f} set={set} options={options || {}} required={false} anchor={(k) => k} companies={companiesOf(f, options)} />
      </FormSection>
      <FormSection step={3} title="Screening">
        <ScreeningQuestionFields f={f} set={set} anchor={(k) => k} companies={companiesOf(f, options)} />
      </FormSection>
      <FormSection step={4} title="Completion & Documents">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Completion Date" required field="completedOn" hint="The day QM completed the screening.">
            <input type="date" value={f.completedOn} onChange={(e) => set('completedOn', e.target.value)} className={inputCls} />
          </Field>
        </div>
        <Field
          label="Screening Document"
          hint={currentScreening
            ? `Current: ${currentScreening.originalName}. Choosing a file replaces it — the previous version is kept.`
            : hintOf('pdfOnly')}
        >
          <FilePicker kind="pdfOnly" onChange={(e) => setScreeningFile((e.target.files || [])[0] || null)} files={screeningFile} />
        </Field>
        {current && supporting.length > 0 && onToggleRemove && (
          <EditableAttachments
            files={supporting}
            removeIds={removeIds}
            onToggle={onToggleRemove}
            label="Current Supporting Files"
            downloadHref={(fl) => `${API_BASE_URL}/api/qm/screening-records/${current.id}/files/${fl.id}`}
          />
        )}
        <Field label="Supporting Files" hint={hintOf('docsSheetsImages', { multiple: true })}>
          <FilePicker multiple kind="docsSheetsImages"
            onChange={(e) => setFiles(Array.from(e.target.files || []))} files={files} />
        </Field>
        <Field label="Remarks">
          <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
        </Field>
      </FormSection>
    </>
  );
};

export const RECORD_SOURCE_LABEL = {
  'workflow:screening': 'Screening request',
};

export const ScreeningRecordSourceBadge = ({ record }) => (
  record.source === 'workflow:screening' ? (
    <span className="inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700">
      {record.sourceRequestNo || 'Screening request'}
    </span>
  ) : null
);
